import { getBearer } from "./tokens";
import { getApiHost } from "./tenant";
import type { PinSnapshot } from "./pin";

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

export function humanizeApiError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return "Your session has expired. Please sign in again.";
    if (e.status === 403) return "Access denied. Contact support if this seems wrong.";
    if (e.status === 404) return "Session not found — it may have expired. Try starting a new chat.";
    if (e.status === 429) return "Too many requests — wait a moment and try again.";
    if (e.status >= 500) return "myai is having trouble right now. Try again in a moment.";
    return `Something went wrong (error ${e.status}).`;
  }
  if (e instanceof TypeError && e.message.includes("fetch")) {
    return "Can't reach myai — check your internet connection.";
  }
  return e instanceof Error ? e.message : "An unexpected error occurred.";
}

// Persistent banner-worthy errors (network failure, server outage, rate limit)
// vs transient toasts (one-off 4xx, etc).
export function isPersistentError(e: unknown): boolean {
  if (e instanceof ApiError) return e.status >= 500 || e.status === 429;
  if (e instanceof TypeError && e.message.includes("fetch")) return true;
  return false;
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const bearer = getBearer();
  if (!bearer) throw new Error("apiFetch: no bearer token (sign in first)");

  const res = await fetch(`${getApiHost()}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, body || res.statusText);
  }

  return (await res.json()) as T;
}

// =============================================================================
// external_session wrappers
// =============================================================================

export type Selection = {
  workbookName: string;
  sheetName: string;
  address: string;
  rowCount: number;
  columnCount: number;
  values: unknown[][];
};

export type CallerMetadata = {
  client: "excel-addin";
  client_version: string;
  workbook_name?: string;
  sheet_name?: string;
  selection?: Selection;
  pin?: PinSnapshot;
};

export type StartSessionResponse = { session_id: string };

export type SendMessageRequest = {
  text: string;
  caller_metadata?: CallerMetadata;
  wait?: boolean;
  wait_seconds?: number;
};

export type SendMessageResponse = {
  message_id: string;
  status: string;
  response?: { text?: string; history_length_after?: number };
  response_error?: string;
};

const CLIENT_VERSION = "0.1.0";

export async function startSession(): Promise<StartSessionResponse> {
  return apiFetch<StartSessionResponse>("/api/external_session/start", {
    method: "POST",
    body: JSON.stringify({
      caller_metadata: { client: "excel-addin", client_version: CLIENT_VERSION },
    }),
  });
}

export async function sendMessage(
  sessionId: string,
  body: SendMessageRequest,
): Promise<SendMessageResponse> {
  return apiFetch<SendMessageResponse>(
    `/api/external_session/${sessionId}/send_message`,
    {
      method: "POST",
      body: JSON.stringify({
        wait: true,
        wait_seconds: 30,
        ...body,
      }),
    },
  );
}

export async function getMessageResponse(
  sessionId: string,
  messageId: string,
  waitSeconds = 30,
): Promise<NonNullable<SendMessageResponse["response"]>> {
  return apiFetch<NonNullable<SendMessageResponse["response"]>>(
    `/api/external_session/${sessionId}/messages/${messageId}/response?wait_seconds=${waitSeconds}`,
  );
}
